import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Calculator, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI, entitiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Receivables Breakdown diagnostic (Fix 1).
 *
 * Decomposes an entity's Assets & Liabilities Current Assets receivables into
 * its canonical components, so the corrected components sum exactly to the
 * corrected A&L figure and the double-counted overlap is explicit.
 */
function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function ReceivablesBreakdownPanel() {
  var [entities, setEntities] = useState([]);
  var [selectedId, setSelectedId] = useState('br_ltd');
  var [loading, setLoading] = useState(false);
  var [result, setResult] = useState(null);

  useEffect(function () {
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {});
  }, []);

  var run = async function () {
    if (!selectedId) return;
    setLoading(true);
    setResult(null);
    try {
      var res = await diagnosticsAPI.receivablesBreakdown(selectedId);
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var bros = result?.components?.bros_client_invoices;
  var unpaid = result?.components?.unpaid_invoices;
  var overlap = result?.overlap_removed;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          Receivables Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Decomposes A&amp;L Current Assets receivables into canonical components.
          Corrected components sum to the corrected receivables total; the overlap
          is the BR OS twin set that previously double-counted.
        </p>
        <div className="flex items-center gap-2">
          <select
            className="text-sm border rounded px-2 py-1.5 bg-background flex-1"
            value={selectedId}
            onChange={function (e) { setSelectedId(e.target.value); }}
          >
            <option value="">Select entity...</option>
            {entities.map(function (ent) {
              return <option key={ent.id} value={ent.id}>{ent.name}</option>;
            })}
          </select>
          <Button size="sm" onClick={run} disabled={loading || !selectedId}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
          </Button>
        </div>

        {result && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{result.entity_name}</span>
              <Badge variant="outline" className="font-mono text-xs">{result.entity_id}</Badge>
              <span className="text-xs text-muted-foreground">display {result.display_currency}</span>
            </div>

            {/* Corrected total */}
            <div className="bg-muted/50 rounded p-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Corrected receivables total</span>
                <span className="font-mono font-semibold">{fmt(result.corrected_receivables_total)}</span>
              </div>
              {result.corrected_total_unconvertible && (
                <div className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> some components unconvertible; total partial
                </div>
              )}
            </div>

            {/* Component: BR OS client invoices */}
            {bros?.applicable && (
              <div>
                <h4 className="font-semibold mb-1">BR OS client invoices (bros_invoices)</h4>
                <div className="bg-muted/50 rounded p-3 space-y-1 font-mono text-xs">
                  <div className="flex justify-between"><span>count</span><span>{bros.count}</span></div>
                  {Object.entries(bros.by_currency || {}).map(function (entry) {
                    var c = entry[0]; var v = entry[1];
                    return (
                      <div key={c} className="flex justify-between">
                        <span>native {c} ({v.count})</span>
                        <span>{fmt(v.sum_native)} {c}{v.converted_value != null ? ' → ' + fmt(v.converted_value) : ''}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between border-t border-border/40 pt-1"><span>converted total</span><span>{fmt(bros.converted_value)}</span></div>
                  {(bros.unconverted_remainder || []).map(function (u) {
                    return (
                      <div key={u.currency} className="text-amber-600 flex justify-between">
                        <span>unconverted {u.currency}</span><span>{fmt(u.native_value)} {u.currency}</span>
                      </div>
                    );
                  })}
                  {bros.missing_total_amount > 0 && (
                    <div className="text-amber-600">missing total_amount: {bros.missing_total_amount}</div>
                  )}
                </div>
                <table className="text-xs w-full border-collapse mt-2">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">status</th>
                      <th className="py-1 pr-2">count</th>
                      <th className="py-1 pr-2">outstanding (native by ccy)</th>
                      <th className="py-1 pr-2">counts?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(bros.by_status || {}).map(function (entry) {
                      var s = entry[0]; var v = entry[1];
                      var ccy = Object.entries(v.by_currency || {}).map(function (e) { return fmt(e[1]) + ' ' + e[0]; }).join('; ');
                      return (
                        <tr key={s} className="border-b border-border/30">
                          <td className="py-1 pr-2 font-mono">{s}</td>
                          <td className="py-1 pr-2 font-mono">{v.count}</td>
                          <td className="py-1 pr-2 font-mono">{ccy || '-'}</td>
                          <td className="py-1 pr-2">{v.counts_as_receivable
                            ? <Badge variant="default" className="text-[10px]">yes</Badge>
                            : <Badge variant="outline" className="text-[10px] text-muted-foreground">excluded</Badge>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Verdict */}
                {bros.verdict && (
                  <div className="mt-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-2 text-xs">
                    {bros.verdict}
                  </div>
                )}

                {/* Outstanding by issue year */}
                {bros.by_issue_year && Object.keys(bros.by_issue_year).length > 0 && (
                  <table className="text-xs w-full border-collapse mt-2">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">issue year</th>
                        <th className="py-1 pr-2">count</th>
                        <th className="py-1 pr-2">outstanding (native by ccy)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(bros.by_issue_year).map(function (entry) {
                        var y = entry[0]; var v = entry[1];
                        var ccy = Object.entries(v.by_currency || {}).map(function (e) { return fmt(e[1]) + ' ' + e[0]; }).join('; ');
                        return (
                          <tr key={y} className="border-b border-border/30">
                            <td className="py-1 pr-2 font-mono">{y}</td>
                            <td className="py-1 pr-2 font-mono">{v.count}</td>
                            <td className="py-1 pr-2 font-mono">{ccy || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Top 20 outstanding invoices with candidate payments */}
                {bros.top_outstanding && bros.top_outstanding.length > 0 && (
                  <div className="mt-3">
                    <h5 className="font-semibold text-xs mb-1">Top {bros.top_outstanding.length} by outstanding (with candidate payment)</h5>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-1 pr-2">number</th>
                            <th className="py-1 pr-2">counterparty</th>
                            <th className="py-1 pr-2">issue</th>
                            <th className="py-1 pr-2">status</th>
                            <th className="py-1 pr-2">ccy</th>
                            <th className="py-1 pr-2 text-right">outstanding (native)</th>
                            <th className="py-1 pr-2 text-right">converted</th>
                            <th className="py-1 pr-2">candidate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bros.top_outstanding.map(function (r) {
                            var cand = r.candidate_payment;
                            return (
                              <tr key={r.id} className="border-b border-border/30">
                                <td className="py-1 pr-2 font-mono">{r.number}</td>
                                <td className="py-1 pr-2 truncate max-w-[140px]" title={r.counterparty}>{r.counterparty}</td>
                                <td className="py-1 pr-2 font-mono">{(r.issue_date || '').slice(0, 10)}</td>
                                <td className="py-1 pr-2 font-mono">{r.status}</td>
                                <td className="py-1 pr-2 font-mono">{r.currency}</td>
                                <td className="py-1 pr-2 font-mono text-right">{fmt(r.outstanding)} {r.currency}</td>
                                <td className="py-1 pr-2 font-mono text-right">{r.converted_value != null ? fmt(r.converted_value) : '-'}</td>
                                <td className="py-1 pr-2">
                                  {cand ? (
                                    <span className="flex items-center gap-1">
                                      <Badge variant={cand.is_reconciled ? 'default' : 'outline'} className="text-[10px]">
                                        {cand.is_reconciled ? 'reconciled' : 'unreconciled'}
                                      </Badge>
                                      <span className="font-mono text-[10px] text-muted-foreground">{fmt(cand.amount)} {(cand.date || '').slice(0, 10)}</span>
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground text-[10px]">none</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Component: Unpaid invoices (db.invoices, source != bros) */}
            <div>
              <h4 className="font-semibold mb-1">Unpaid invoices (db.invoices, source != bros)</h4>
              <div className="bg-muted/50 rounded p-3 space-y-1 font-mono text-xs">
                <div className="flex justify-between"><span>receivable count</span><span>{unpaid.count}</span></div>
                <div className="flex justify-between"><span>native ({unpaid.currency})</span><span>{fmt(unpaid.sum_native)}</span></div>
                <div className="flex justify-between"><span>converted</span><span>{fmt(unpaid.converted_value)}</span></div>
              </div>
              {Object.keys(unpaid.by_entity_id || {}).length > 0 && (
                <div className="text-xs mt-1">
                  by entity_id:{' '}
                  {Object.entries(unpaid.by_entity_id).map(function (e) {
                    return <span key={e[0]} className="font-mono mr-2">{e[0]}={e[1].count}/{fmt(e[1].sum)}</span>;
                  })}
                </div>
              )}
            </div>

            {/* Overlap removed */}
            <div>
              <h4 className="font-semibold mb-1 flex items-center gap-2">
                Overlap removed (double-count)
                {overlap.count > 0 && <Badge variant="destructive" className="text-[10px]">{overlap.count} twins</Badge>}
              </h4>
              <div className="bg-muted/50 rounded p-3 space-y-1 font-mono text-xs">
                <div className="text-muted-foreground text-[11px]">{overlap.description}</div>
                <div className="flex justify-between"><span>count</span><span>{overlap.count}</span></div>
                <div className="flex justify-between"><span>native ({overlap.currency})</span><span>{fmt(overlap.sum_native)}</span></div>
                <div className="flex justify-between"><span>converted (was double-counted)</span><span>{fmt(overlap.converted_value)}</span></div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
